import { useMemo } from "react"
import type { CombinedError, UseQueryExecute } from "urql"
import { useQuery } from "urql"

import type { CompetitorProduct, Product, Query } from "@/graphql/codegen/graphql"
import { EquivalencyGroupProductListDocument } from "@/graphql/codegen/graphql"

interface UseEquivalencyGroupProductsReturn {
  products: Product[]
  competitorProducts: CompetitorProduct[]
  fetching: boolean
  error?: CombinedError
  refetchProductsList: UseQueryExecute
}

const useEquivalencyGroupProducts = ({
  equivalencyGroupId,
  pause = false,
}: {
  equivalencyGroupId: string
  pause?: boolean
}): UseEquivalencyGroupProductsReturn => {
  const [{ error, data, fetching }, refetchProductsList] = useQuery<Pick<Query, "equivalencyGroup">>({
    query: EquivalencyGroupProductListDocument,
    variables: {
      input: {
        filter: {
          equivalencyGroupId,
        },
      },
    },
    pause,
  })

  const products = useMemo(() => {
    if (!data?.equivalencyGroup?.equivalencyGroupProductList) return []
    if (data.equivalencyGroup.equivalencyGroupProductList.__typename === "EquivalencyGroupProductListSuccess") {
      return (data.equivalencyGroup.equivalencyGroupProductList.products || []) as Product[]
    }
    return []
  }, [data])

  const competitorProducts = useMemo(() => {
    if (!data?.equivalencyGroup?.equivalencyGroupProductList) return []
    if (data.equivalencyGroup.equivalencyGroupProductList.__typename === "EquivalencyGroupProductListSuccess") {
      return (data.equivalencyGroup.equivalencyGroupProductList.competitorProducts || []) as CompetitorProduct[]
    }
    return []
  }, [data])

  return {
    products,
    competitorProducts,
    fetching,
    error,
    refetchProductsList,
  }
}

export default useEquivalencyGroupProducts
